import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coins, TrendingUpIcon, ArrowRightIcon, DollarSign, Award, Trophy, ClockIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import SnapCard from '@/components/SnapCard';
import type { Snap } from '@/types/database.types';
import { getActivityThresholds, getSnapActivity } from '@/utils/snap';
import Button from '@/components/form/Button';
import { Link } from 'react-router-dom';

export default function CashSnaps() {
  const [cashSnaps, setCashSnaps] = useState<Snap[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchCashSnaps = async () => {
      try {
        setLoading(true);
        
        // Get CashSnaps
        const { data, error } = await supabase
          .from('snaps')
          .select(`
            id,
            title,
            description,
            total_votes,
            view_count,
            likes_count,
            created_at,
            ends_at,
            is_system,
            cashsnap,
            creator_id,
            creator:user_profiles!inner(
              id,
              username,
              avatar_url,
              created_at,
              followers_count
            ),
            options:snap_options!snap_options_snap_id_fkey(
              id,
              title,
              description,
              image_url,
              vote_count
            ),
            comments:comments(count),
            categories:snap_categories(category)
          `)
          .eq('is_private', false) // Only public SNAPs
          .eq('cashsnap', true) // Only CashSnaps
          .order('created_at', { ascending: false }) // Order by creation date
          .limit(3);

        if (error) throw error;

        // Format the data
        const formattedSnaps = data.map(snap => ({
          ...snap,
          categories: snap.categories?.map((c: any) => c.category) || [],
          commentCount: snap.comments?.[0]?.count || 0,
          isOwner: user?.id === snap.creator_id,
          userLiked: false, // Default value, we don't track this for non-logged in users
          isMarked: false
        }));

        setCashSnaps(formattedSnaps);
      } catch (error) {
        console.error('Error fetching CashSnaps:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCashSnaps();
  }, [user]);

  const handleSnapClick = (snapId: string) => {
    if (user) {
      // If user is logged in, navigate directly to the SNAP
      navigate(`/snap/${snapId}`);
    } else {
      // If user is not logged in, store the SNAP ID in localStorage and redirect to login
      localStorage.setItem('redirectSnapId', snapId);
      navigate('/login');
    }
  };

  const handleDelete = () => {
    // This is just a placeholder - deletion won't be available on the landing page
    console.log("Delete operation not available on landing page");
  };

  const handleLike = (id: string, event: React.MouseEvent) => {
    // This is just a placeholder - liking won't be available on the landing page
    event.stopPropagation();
    console.log("Like operation not available on landing page");
  };

  // Calculate activity thresholds for proper display
  const thresholds = getActivityThresholds(cashSnaps);

  if (loading) {
    return (
      <div id="cashsnaps-section" className="py-16 bg-gradient-to-b from-white to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-yellow-600 font-semibold tracking-wide uppercase">Win Real Rewards</h2>
            <h1 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              CashSnaps
            </h1>
          </div>
          <div className="mt-10 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (cashSnaps.length === 0) {
    return null;
  }

  return (
    <div id="cashsnaps-section" className="py-16 bg-gradient-to-b from-white to-yellow-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-yellow-100 text-yellow-800 shadow-md mb-4">
            <Coins className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Win Real Rewards</span>
          </div>
          <h1 className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            CashSnaps
          </h1>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
            Make predictions and earn rewards for correct answers
          </p>
        </motion.div>

        <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 text-center"
            >
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-6 rounded-xl shadow-sm max-w-3xl mx-auto">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">How CashSnaps Work</h3>
                <p className="text-yellow-700 mb-4">
                  Predict outcomes correctly and earn real rewards! The more active you are on the platform, 
                  the bigger your share of the prize pool.
                </p>
                <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2 text-yellow-600" />
                    Early Voting Bonus
                  </h4>
                  <p className="text-sm text-gray-700">
                    Vote early to maximize your potential winnings! Early voters receive up to 100% of their activity score, 
                    while late voters receive as little as 5%. See our <Link to="/conditions" className="text-blue-600 hover:underline">Conditions of Participation</Link> for details.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/cashsnaps')}
                  variant="primary"
                  className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                >
                  Learn More
                </Button>
              </div>
        </motion.div>

        {cashSnaps.length > 0 && (
          <div className="mt-16">
            <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {cashSnaps.map((snap, index) => (
                <motion.div
                  key={snap.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="cursor-pointer h-full"
                >
                  <SnapCard
                    snap={{
                      ...snap,
                      activity: getSnapActivity(snap, thresholds)
                    }}
                    onDelete={handleDelete}
                    onLike={handleLike}
                  />
                </motion.div>
              ))}
            </div>


          </div>
        )}
        

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12"
        >
          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-8 rounded-xl shadow-sm max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                  <DollarSign className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Real Rewards</h3>
                <p className="text-yellow-700 text-sm">
                  Win actual prizes for making correct predictions
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Award className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Activity Bonus</h3>
                <p className="text-yellow-700 text-sm">
                  The more active you are, the bigger your potential reward
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                  <Trophy className="h-8 w-8 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Leaderboards</h3>
                <p className="text-yellow-700 text-sm">
                  Compete with others and climb the prediction rankings
                </p>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Button
                onClick={() => navigate('/cashsnaps')}
                variant="primary"
                className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
              >
                Learn More About CashSnaps
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </motion.div>
        
      </div>
    </div>
  );
}